import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { zodiacLabel } from "@/lib/zodiac";
import {
  ArrowLeft,
  Mail,
  Phone,
  Instagram,
  MapPin,
  Ticket as TicketIcon,
  Wine,
  CalendarDays,
  CreditCard,
  Coins,
  Package,
  CheckCircle2,
  XCircle,
} from "lucide-react";

export const Route = createFileRoute("/harikaradmin/members/$userId")({
  head: () => ({ meta: [{ title: "Üye Detayı — JUËN" }] }),
  component: MemberDetailPage,
});

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  instagram: string | null;
  city: string | null;
  bio: string | null;
  gender: string | null;
  membership_status: string;
  points_balance: number;
  created_at: string;
  photo_url: string | null;
}

interface TicketRow {
  id: string;
  code: string;
  created_at: string;
  checked_in_at: string | null;
  attendee_name: string | null;
  events: { id: string; name: string; starts_at: string; venue: string | null } | null;
  ticket_types: { name: string } | null;
}

interface VoucherRow {
  id: string;
  code: string;
  created_at: string;
  redeemed_at: string | null;
  events: { id: string; name: string; starts_at: string } | null;
  drinks: { name: string } | null;
}

interface OrderRow {
  id: string;
  quantity: number;
  total_kurus: number;
  status: string;
  payment_method: string | null;
  points_used: number | null;
  created_at: string;
  paid_at: string | null;
  events: { name: string; starts_at: string } | null;
  ticket_types: { name: string } | null;
}

interface ReservationRow {
  id: string;
  party_size: number;
  preferred_date: string;
  notes: string | null;
  status: string;
  created_at: string;
  events: { name: string; starts_at: string } | null;
}

interface PointTxnRow {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  created_at: string;
}

interface PointPkgOrderRow {
  id: string;
  points: number;
  price_kurus: number;
  status: string;
  created_at: string;
  paid_at?: string | null;
  point_packages: { name: string } | null;
}

const fmtTL = (kurus: number) => `${(kurus / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`;
const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "—";

function MemberDetailPage() {
  const { userId } = Route.useParams();
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [reservations, setReservations] = useState<ReservationRow[]>([]);
  const [pointTxns, setPointTxns] = useState<PointTxnRow[]>([]);
  const [pkgOrders, setPkgOrders] = useState<PointPkgOrderRow[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin || !userId) return;
    void (async () => {
      setPageLoading(true);
      const [p, t, v, o, r, pt, pp] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", userId).maybeSingle(),
        supabase
          .from("tickets")
          .select("id,code,created_at,checked_in_at,attendee_name,events(id,name,starts_at,venue),ticket_types(name)")
          .eq("user_id", userId)
          .order("created_at", { ascending: false }),
        supabase
          .from("drink_vouchers")
          .select("id,code,created_at,redeemed_at,events(id,name,starts_at),drinks(name)")
          .eq("user_id", userId)
          .order("created_at", { ascending: false }),
        supabase
          .from("orders")
          .select("id,quantity,total_kurus,status,payment_method,points_used,created_at,paid_at,events(name,starts_at),ticket_types(name)")
          .eq("user_id", userId)
          .order("created_at", { ascending: false }),
        supabase
          .from("reservations")
          .select("id,party_size,preferred_date,notes,status,created_at,events(name,starts_at)")
          .eq("user_id", userId)
          .order("created_at", { ascending: false }),
        supabase
          .from("point_transactions")
          .select("id,amount,type,description,created_at")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("point_package_orders")
          .select("id,points,price_kurus,status,created_at,point_packages(name)")
          .eq("user_id", userId)
          .order("created_at", { ascending: false }),
      ]);

      if (p.error) toast.error(p.error.message);
      setProfile((p.data as Profile | null) ?? null);
      setTickets((t.data as unknown as TicketRow[]) ?? []);
      setVouchers((v.data as unknown as VoucherRow[]) ?? []);
      setOrders((o.data as unknown as OrderRow[]) ?? []);
      setReservations((r.data as unknown as ReservationRow[]) ?? []);
      setPointTxns((pt.data as unknown as PointTxnRow[]) ?? []);
      setPkgOrders((pp.data as unknown as PointPkgOrderRow[]) ?? []);
      setPageLoading(false);
    })();
  }, [isAdmin, userId]);

  if (loading || !isAdmin || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Üye bulunamadı.</div>
      </div>
    );
  }

  // Group tickets & vouchers by event
  const byEvent = new Map<string, { name: string; starts_at: string; tickets: TicketRow[]; vouchers: VoucherRow[] }>();
  for (const t of tickets) {
    const key = t.events?.id ?? "other";
    if (!byEvent.has(key))
      byEvent.set(key, {
        name: t.events?.name ?? "—",
        starts_at: t.events?.starts_at ?? "",
        tickets: [],
        vouchers: [],
      });
    byEvent.get(key)!.tickets.push(t);
  }
  for (const v of vouchers) {
    const key = v.events?.id ?? "other";
    if (!byEvent.has(key))
      byEvent.set(key, {
        name: v.events?.name ?? "—",
        starts_at: v.events?.starts_at ?? "",
        tickets: [],
        vouchers: [],
      });
    byEvent.get(key)!.vouchers.push(v);
  }
  const eventGroups = Array.from(byEvent.entries()).sort(
    (a, b) => (b[1].starts_at || "").localeCompare(a[1].starts_at || ""),
  );

  const redeemedCount = vouchers.filter((v) => v.redeemed_at).length;
  const checkedInCount = tickets.filter((t) => t.checked_in_at).length;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <Link
          to="/harikaradmin"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Üyeler
        </Link>

        <AdminTabs />

        {/* Profile card */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-start gap-4">
            {profile.photo_url ? (
              <img
                src={profile.photo_url}
                alt=""
                className="h-20 w-20 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted text-lg font-semibold text-muted-foreground">
                {profile.first_name?.[0]}
                {profile.last_name?.[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                {profile.first_name} {profile.last_name}
              </h1>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" /> {profile.email}
                </span>
                {profile.phone && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" /> {profile.phone}
                  </span>
                )}
                {profile.instagram && (
                  <a
                    href={`https://instagram.com/${profile.instagram.replace(/^@/, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline"
                  >
                    <Instagram className="h-3.5 w-3.5" /> {profile.instagram}
                  </a>
                )}
                {profile.city && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {profile.city}
                  </span>
                )}
                {profile.birth_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {new Date(profile.birth_date).toLocaleDateString("tr-TR")} · {zodiacLabel(profile.birth_date)}
                  </span>
                )}
              </div>
              {profile.bio && <p className="mt-3 text-sm">{profile.bio}</p>}
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3 text-center">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Bakiye</div>
              <div className="mt-1 text-2xl font-bold text-primary">{profile.points_balance ?? 0} J</div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat icon={TicketIcon} label="Bilet" value={tickets.length} />
            <Stat icon={CheckCircle2} label="Giriş" value={checkedInCount} />
            <Stat icon={Wine} label="İçecek" value={vouchers.length} sub={`${redeemedCount} kullanıldı`} />
            <Stat icon={CalendarDays} label="Etkinlik" value={eventGroups.length} />
          </div>
        </div>

        {/* Events breakdown */}
        <Section title="Etkinlik geçmişi" icon={CalendarDays}>
          {eventGroups.length === 0 ? (
            <Empty>Henüz etkinlik geçmişi yok.</Empty>
          ) : (
            <div className="space-y-4">
              {eventGroups.map(([key, g]) => (
                <div key={key} className="rounded-xl border border-border bg-background p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-base font-semibold">{g.name}</h3>
                    {g.starts_at && (
                      <span className="text-xs text-muted-foreground">{fmtDate(g.starts_at)}</span>
                    )}
                  </div>

                  {g.tickets.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Biletler ({g.tickets.length})
                      </div>
                      <div className="space-y-1.5">
                        {g.tickets.map((t) => (
                          <div
                            key={t.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
                          >
                            <div className="min-w-0">
                              <span className="font-medium">{t.ticket_types?.name ?? "Bilet"}</span>
                              {t.attendee_name && (
                                <span className="text-muted-foreground"> · {t.attendee_name}</span>
                              )}
                              <span className="ml-2 font-mono text-xs text-muted-foreground">{t.code}</span>
                            </div>
                            {t.checked_in_at ? (
                              <span className="inline-flex items-center gap-1 rounded-md bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                                <CheckCircle2 className="h-3 w-3" /> Giriş: {fmtDate(t.checked_in_at)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                <XCircle className="h-3 w-3" /> Giriş yok
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {g.vouchers.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        İçecekler ({g.vouchers.length})
                      </div>
                      <div className="space-y-1.5">
                        {g.vouchers.map((v) => (
                          <div
                            key={v.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
                          >
                            <div className="min-w-0">
                              <Wine className="mr-1.5 inline h-3.5 w-3.5 text-primary" />
                              <span className="font-medium">{v.drinks?.name ?? "İçecek"}</span>
                              <span className="ml-2 font-mono text-xs text-muted-foreground">{v.code}</span>
                            </div>
                            {v.redeemed_at ? (
                              <span className="inline-flex items-center gap-1 rounded-md bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                                <CheckCircle2 className="h-3 w-3" /> İçildi: {fmtDate(v.redeemed_at)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                Kullanılmadı
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Orders */}
        <Section title="Bilet siparişleri" icon={CreditCard}>
          {orders.length === 0 ? (
            <Empty>Sipariş yok.</Empty>
          ) : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div
                  key={o.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{o.events?.name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {o.ticket_types?.name ?? ""} · {o.quantity}x · {fmtDate(o.created_at)}
                      {o.payment_method ? ` · ${o.payment_method}` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{fmtTL(o.total_kurus)}</div>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Reservations */}
        <Section title="Rezervasyonlar" icon={CalendarDays}>
          {reservations.length === 0 ? (
            <Empty>Rezervasyon yok.</Empty>
          ) : (
            <div className="space-y-2">
              {reservations.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{r.events?.name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.party_size} kişi · {fmtDate(r.preferred_date)}
                    </div>
                    {r.notes && <div className="mt-0.5 text-xs italic text-muted-foreground">"{r.notes}"</div>}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* J package orders */}
        <Section title="J paket siparişleri" icon={Package}>
          {pkgOrders.length === 0 ? (
            <Empty>Sipariş yok.</Empty>
          ) : (
            <div className="space-y-2">
              {pkgOrders.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium">
                      {p.point_packages?.name ?? "Paket"} · {p.points} J
                    </div>
                    <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{fmtTL(p.price_kurus)}</div>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Point transactions */}
        <Section title="J hareketleri" icon={Coins}>
          {pointTxns.length === 0 ? (
            <Empty>Hareket yok.</Empty>
          ) : (
            <div className="space-y-1.5">
              {pointTxns.map((pt) => (
                <div
                  key={pt.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">
                      {fmtDate(pt.created_at)} · {pt.type}
                    </div>
                    {pt.description && <div className="text-sm">{pt.description}</div>}
                  </div>
                  <div className={`font-semibold ${pt.amount >= 0 ? "text-success" : "text-destructive"}`}>
                    {pt.amount >= 0 ? "+" : ""}
                    {pt.amount} J
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof TicketIcon;
  label: string;
  value: number;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-background p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 text-xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof TicketIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </h2>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-6 text-center text-sm text-muted-foreground">{children}</div>;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    paid: "bg-success/10 text-success",
    approved: "bg-success/10 text-success",
    confirmed: "bg-success/10 text-success",
    pending: "bg-muted text-muted-foreground",
    awaiting_payment: "bg-muted text-muted-foreground",
    cancelled: "bg-destructive/10 text-destructive",
    rejected: "bg-destructive/10 text-destructive",
    failed: "bg-destructive/10 text-destructive",
  };
  const cls = map[status] ?? "bg-muted text-muted-foreground";
  return <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}
